import { AxiosError } from 'axios';
import { showWarningToast, showErrorToast } from '../utils/toast';

interface RetryConfig {
  maxRetries?: number;
  retryDelay?: number;
  retryCondition?: (error: AxiosError) => boolean;
  onRetry?: (retryCount: number, error: AxiosError) => void;
  exponentialBackoff?: boolean;
}

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryCondition: (error: AxiosError) => {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  },
  onRetry: (retryCount: number) => {
    const isLastRetry = retryCount === DEFAULT_RETRY_CONFIG.maxRetries;
    const message = isLastRetry 
      ? `Última tentativa... (${retryCount}/${DEFAULT_RETRY_CONFIG.maxRetries})`
      : `Tentando novamente... (${retryCount}/${DEFAULT_RETRY_CONFIG.maxRetries})`;
    
    showWarningToast(message);
  },
  exponentialBackoff: true,
};

/**
 * Retry mechanism for failed requests
 * @param fn The function to retry (should return a Promise)
 * @param config Retry configuration
 * @returns Promise with the result or throws the last error
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config?: RetryConfig
): Promise<T> {
  const mergedConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  let lastError: Error | AxiosError | null = null;

  for (let attempt = 0; attempt <= mergedConfig.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error | AxiosError;

      // Check if it's an Axios error
      const isAxiosError = (error as AxiosError).isAxiosError;
      
      if (attempt === mergedConfig.maxRetries) {
        // Last attempt failed
        break;
      }

      if (isAxiosError && !mergedConfig.retryCondition(error as AxiosError)) {
        // Don't retry if condition is not met
        throw error;
      }

      // Call onRetry callback
      if (mergedConfig.onRetry) {
        mergedConfig.onRetry(attempt + 1, error as AxiosError);
      }

      // Calculate delay with optional exponential backoff
      const delay = mergedConfig.exponentialBackoff
        ? mergedConfig.retryDelay * Math.pow(2, attempt)
        : mergedConfig.retryDelay;

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All retries failed
  showErrorToast('Todas as tentativas falharam', {
    action: {
      label: 'Tentar Novamente',
      onClick: () => window.location.reload()
    },
    duration: 8000
  });
  throw lastError;
}

/**
 * Create a retryable version of an async function
 * @param fn The async function to make retryable
 * @param config Retry configuration
 * @returns A new function that will retry on failure
 */
export function makeRetryable<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  config?: RetryConfig
): T {
  return ((...args: Parameters<T>) => {
    return withRetry(() => fn(...args), config);
  }) as T;
}

/**
 * Retry decorator for class methods
 * @param config Retry configuration
 */
export function Retryable(config?: RetryConfig) {
  return function (
    target: unknown,
    propertyKey: string,
    descriptor: PropertyDescriptor
  ) {
    const originalMethod = descriptor.value;

    descriptor.value = async function (...args: unknown[]) {
      return withRetry(
        () => originalMethod.apply(this, args),
        config
      );
    };

    return descriptor;
  };
}

// Specific retry configurations for different scenarios
export const retryConfigs = {
  // For critical operations like payments
  critical: {
    maxRetries: 5,
    retryDelay: 2000,
    exponentialBackoff: true,
    retryCondition: (error: AxiosError) => {
      // Only retry on network errors or timeouts
      return !error.response || error.code === 'ECONNABORTED';
    },
  },

  // For non-critical operations
  standard: {
    maxRetries: 3,
    retryDelay: 1000,
    exponentialBackoff: true,
  },

  // For operations that should fail fast
  fast: {
    maxRetries: 1,
    retryDelay: 500,
    exponentialBackoff: false,
  },

  // For file uploads
  upload: {
    maxRetries: 3,
    retryDelay: 3000,
    exponentialBackoff: true,
    retryCondition: (error: AxiosError) => {
      // Retry on network errors or specific server errors
      return !error.response || 
             error.response.status === 503 || // Service Unavailable
             error.response.status === 504 || // Gateway Timeout
             error.code === 'ECONNABORTED';
    },
  },
};

// Example usage with axios
export function createRetryableAxiosInstance(axiosInstance: Record<string, unknown>, config?: RetryConfig) {
  const retryableInstance = { ...axiosInstance };
  
  ['get', 'delete', 'head', 'options'].forEach((method) => {
    const originalMethod = axiosInstance[method];
    if (typeof originalMethod === 'function') {
      retryableInstance[method] = makeRetryable(originalMethod as (...args: unknown[]) => Promise<unknown>, config);
    }
  });
  
  ['post', 'put', 'patch'].forEach((method) => {
    const originalMethod = axiosInstance[method];
    if (typeof originalMethod === 'function') {
      retryableInstance[method] = makeRetryable(
        originalMethod as (...args: unknown[]) => Promise<unknown>,
        config || retryConfigs.standard
      );
    }
  });
  
  return retryableInstance;
}